/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

import { getUsers } from "./api/users";
import { getSuppliers } from "./api/suppliers";
import { getOrganisms } from "./api/organisms";
import { setAllUsers } from "./redux/reducers/usersReducer";
import { setAllSuppliers } from "./redux/reducers/suppliersReducer";
import { setAllOrganisms } from "./redux/reducers/organismsReducer";
import { useDispatch } from "react-redux";

import { AuthProvider } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

export default function App() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const isAccueilWithToken =
    location.pathname === "/accueil" && token !== null && token !== "" && token !== undefined;

  useAuthCheck(isAccueilWithToken);

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    /* openConfigurator, */
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const dispatchR = useDispatch();
  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  /* const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator); */

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  async function fetchUsers() {
    if (window.location.href.includes("accueil") || window.location.href.includes("connexion"))
      return;
    const response = await getUsers();
    const formatedUsers = await response.data.map((user) => ({
      label: user.name,
      id: user.user_id,
    }));
    dispatchR(setAllUsers(formatedUsers));
  }

  async function fetchSuppliers() {
    if (window.location.href.includes("accueil") || window.location.href.includes("connexion"))
      return;
    const response = await getSuppliers();
    const formatedSuppliers = await response.data.map((supplier) => ({
      label: supplier.supplier_name,
      id: supplier.supplier_id,
    }));
    dispatchR(setAllSuppliers(formatedSuppliers));
  }

  async function fetchOrganisms() {
    if (window.location.href.includes("accueil") || window.location.href.includes("connexion"))
      return;
    const response = await getOrganisms();
    const formatedOrganisms = await response.data.map((organism) => ({
      label: organism.organism_name,
      id: organism.organism_id,
    }));
    dispatchR(setAllOrganisms(formatedOrganisms));
  }

  useEffect(() => {
    fetchUsers();
    fetchSuppliers();
    fetchOrganisms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchR, location.pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  /* const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  ); */

  return (
    <AuthProvider>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="The NGT"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/accueil" />} />
        </Routes>
      </ThemeProvider>
    </AuthProvider>
  );
}
